import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  collection,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

const AdminNewListing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get params from URL
  const listingType = searchParams.get('type'); // 'broker' or 'borrower'
  const targetId = searchParams.get('id'); // broker ID or borrower (loan) ID

  // Form state
  const [businessName, setBusinessName] = useState('');
  const [businessState, setBusinessState] = useState('');
  const [industry, setIndustry] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [ebitda, setEbitda] = useState('');
  const [businessAge, setBusinessAge] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [licenseNeeded, setLicenseNeeded] = useState(false);
  const [realEstateIncluded, setRealEstateIncluded] = useState(null);
  const [realEstateValue, setRealEstateValue] = useState('');

  // Target info
  const [targetName, setTargetName] = useState('');
  const [targetEmail, setTargetEmail] = useState('');

  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prequalId, setPrequalId] = useState(null);

  const businessDocuments = [
    { id: 'taxReturn2022', label: '2022 Business Tax Returns', required: false },
    { id: 'taxReturn2023', label: '2023 Business Tax Returns', required: false },
    { id: 'taxReturn2024', label: '2024 Business Tax Returns', required: false },
    { id: 'ytdPnL', label: '2025 YTD Profit & Loss', required: false },
    { id: 'ytdBalanceSheet', label: '2025 YTD Balance Sheet', required: false },
    { id: 'cim', label: 'Confidential Information Memorandum (CIM)', required: false },
  ];

  useEffect(() => {
    // Fetch target info
    const fetchTargetInfo = async () => {
      if (!targetId || !listingType) return;

      try {
        if (listingType === 'broker') {
          // Fetch broker info
          const brokerRef = doc(db, 'brokers', targetId);
          const brokerSnap = await getDoc(brokerRef);
          if (brokerSnap.exists()) {
            const data = brokerSnap.data();
            setTargetName(data.firstName && data.lastName
              ? `${data.firstName} ${data.lastName}`
              : data.name || data.businessName || 'Unknown Broker');
            setTargetEmail(data.email || '');
          }
        } else {
          // Fetch borrower (loan application) info
          const loanRef = doc(db, 'loanApplications', targetId);
          const loanSnap = await getDoc(loanRef);
          if (loanSnap.exists()) {
            const data = loanSnap.data();
            setTargetName(`${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown Borrower');
            setTargetEmail(data.email || '');
          }
        }
      } catch (error) {
        console.error('Error fetching target info:', error);
      }
    };

    fetchTargetInfo();
  }, [targetId, listingType]);

  const formatCurrency = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits ? `$${Number(digits).toLocaleString()}` : '';
  };

  const handleCurrencyInput = (value, setter) => {
    const digits = value.replace(/\D/g, '');
    setter(digits);
  };

  const handleFileUpload = async (event, docId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Create a temporary ID for the upload path
      const tempId = prequalId || `temp_${Date.now()}`;
      const storageRef = ref(storage, `admin-uploads/${tempId}/${docId}/${file.name}`);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setUploadedFiles(prev => [
        ...prev.filter(f => f.docId !== docId),
        { docId, fileName: file.name, fileUrl: downloadURL, label: businessDocuments.find(d => d.id === docId)?.label }
      ]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (docId) => {
    setUploadedFiles(prev => prev.filter(f => f.docId !== docId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!businessName.trim()) {
      alert('Please enter a business name');
      return;
    }

    setIsSubmitting(true);

    try {
      if (listingType === 'broker') {
        // Create broker prequalification application
        const prequalData = {
          businessName: businessName.trim(),
          businessState,
          industry,
          projectedSalePrice: purchasePrice ? Number(purchasePrice) : null,
          ebitda: ebitda ? Number(ebitda) : null,
          businessAge,
          businessDescription,
          licenseNeeded,
          realEstateIncluded,
          realEstateValue: realEstateValue ? Number(realEstateValue) : null,
          status: uploadedFiles.length > 0 ? 'pending' : 'Not started',
          brokerId: targetId,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          createdBy: 'admin',
          documents: uploadedFiles.map(f => ({
            documentName: f.label,
            fileName: f.fileName,
            fileUrl: f.fileUrl,
            label: f.label,
            uploadedAt: new Date().toISOString(),
            status: 'uploaded'
          }))
        };

        // Add to prequalificationApplications collection
        const docRef = await addDoc(collection(db, 'prequalificationApplications'), prequalData);

        // Save documents to prequalificationMetadata collection
        if (uploadedFiles.length > 0) {
          for (const file of uploadedFiles) {
            await addDoc(collection(db, 'prequalificationMetadata'), {
              applicationId: docRef.id,
              brokerId: targetId,
              documentName: file.label.includes('Tax Return') ? 'Business Tax Returns' : file.label.includes('P&L') ? 'PnL' : file.label.includes('Balance') ? 'Balance Sheet' : file.label.includes('CIM') ? 'CIM' : 'Other',
              fileName: file.fileName,
              fileUrl: file.fileUrl,
              label: file.label,
              uploadedAt: new Date().toISOString(),
              status: 'uploaded',
              uploadedBy: 'admin'
            });
          }
        }

        // Add to broker's list
        const brokerRef = doc(db, 'brokers', targetId);
        await setDoc(brokerRef, {
          prequalificationApplications: arrayUnion(docRef.id)
        }, { merge: true });

        // Navigate to prequalification details
        navigate(`/prequalification-details/${docRef.id}`);

      } else {
        // Add business to borrower's loan application
        const businessData = {
          businessName: businessName.trim(),
          businessState,
          industry,
          purchasePrice: purchasePrice ? Number(purchasePrice) : null,
          ebitda: ebitda ? Number(ebitda) : null,
          businessAge,
          businessDescription,
          businessSummary: businessDescription,
          licenseNeeded,
          realEstateIncluded,
          realEstateValue: realEstateValue ? Number(realEstateValue) : null,
          loanSize: purchasePrice ? Number(purchasePrice) : null,
          addedAt: new Date().toISOString(),
          addedBy: 'admin',
          prequalified: false,
          businessId: `business_${Date.now()}`
        };

        // Get current loan application
        const loanRef = doc(db, 'loanApplications', targetId);
        const loanSnap = await getDoc(loanRef);

        if (loanSnap.exists()) {
          const currentBusinesses = loanSnap.data().businesses || [];
          const businessIndex = currentBusinesses.length; // Index of the new business

          await updateDoc(loanRef, {
            businesses: [...currentBusinesses, businessData],
            lastUpdated: serverTimestamp()
          });

          // Save uploaded documents to documentMetadata collection for the borrower
          if (uploadedFiles.length > 0) {
            for (const file of uploadedFiles) {
              await addDoc(collection(db, 'documentMetadata'), {
                loanApplicationId: targetId,
                businessIndex: businessIndex,
                documentName: file.label.includes('Tax Return') ? 'Business Tax Returns' : file.label.includes('P&L') ? 'PnL' : file.label.includes('Balance') ? 'Balance Sheet' : 'Other',
                fileName: file.fileName,
                fileUrl: file.fileUrl,
                label: file.label,
                uploadedAt: new Date().toISOString(),
                status: 'uploaded',
                uploadedBy: 'admin'
              });
            }
          }
        }

        // Navigate to loan details
        navigate(`/loan-details/${targetId}`);
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      alert('Error creating listing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!listingType || !targetId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Invalid listing request</h2>
          <p className="text-gray-500 mt-2">Missing required parameters</p>
          <button
            onClick={() => navigate('/ben-portal')}
            className="mt-4 text-indigo-600 hover:text-indigo-800"
          >
            Return to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/ben-portal')}
              className="mr-4 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              {listingType === 'broker' ? (
                <BuildingOfficeIcon className="h-8 w-8 text-indigo-600 mr-3" />
              ) : (
                <UserGroupIcon className="h-8 w-8 text-green-600 mr-3" />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  New {listingType === 'broker' ? 'Broker' : 'Borrower'} Listing
                </h1>
                <p className="text-sm text-gray-500">
                  For: {targetName} {targetEmail && `(${targetEmail})`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Business Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={businessState}
                  onChange={(e) => setBusinessState(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Select state...</option>
                  {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="e.g., Restaurant, Manufacturing"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Price / Loan Size
                </label>
                <input
                  type="text"
                  value={purchasePrice ? formatCurrency(purchasePrice) : ''}
                  onChange={(e) => handleCurrencyInput(e.target.value, setPurchasePrice)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="$0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EBITDA / Cash Flow
                </label>
                <input
                  type="text"
                  value={ebitda ? formatCurrency(ebitda) : ''}
                  onChange={(e) => handleCurrencyInput(e.target.value, setEbitda)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="$0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Age
                </label>
                <select
                  value={businessAge}
                  onChange={(e) => setBusinessAge(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Select...</option>
                  <option value="Less than 2 years">Less than 2 years</option>
                  <option value="2-5 years">2-5 years</option>
                  <option value="5-10 years">5-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Description
                </label>
                <textarea
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Brief description of the business..."
                />
              </div>
            </div>

            {/* Additional Questions */}
            <div className="mt-6 space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="licenseNeeded"
                  checked={licenseNeeded}
                  onChange={(e) => setLicenseNeeded(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="licenseNeeded" className="ml-2 text-sm text-gray-700">
                  Special license required to operate
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Does this include real estate?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="realEstate"
                      checked={realEstateIncluded === true}
                      onChange={() => setRealEstateIncluded(true)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="realEstate"
                      checked={realEstateIncluded === false}
                      onChange={() => setRealEstateIncluded(false)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">No</span>
                  </label>
                </div>
                {realEstateIncluded && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Real Estate Value
                    </label>
                    <input
                      type="text"
                      value={realEstateValue ? formatCurrency(realEstateValue) : ''}
                      onChange={(e) => handleCurrencyInput(e.target.value, setRealEstateValue)}
                      className="w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="$0"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Document Upload - For both Broker and Borrower listings */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
            <p className="text-sm text-gray-500 mb-4">
              Upload financial documents for prequalification analysis. You can also upload these later.
            </p>

            <div className="space-y-4">
              {businessDocuments.map((docType) => {
                const uploadedFile = uploadedFiles.find(f => f.docId === docType.id);

                return (
                  <div key={docType.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-700">{docType.label}</span>
                    </div>

                    {uploadedFile ? (
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm text-green-700 mr-2">{uploadedFile.fileName}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(docType.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <span className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                          <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                          Upload
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.png,.xlsx,.xls"
                          onChange={(e) => handleFileUpload(e, docType.id)}
                          disabled={isUploading}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/ben-portal')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className={`px-6 py-2 rounded-md text-white ${
                listingType === 'broker'
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-green-600 hover:bg-green-700'
              } disabled:bg-gray-400 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? 'Creating...' : `Create ${listingType === 'broker' ? 'Broker' : 'Borrower'} Listing`}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminNewListing;
